import { DataItem, ScheduledJob } from "@/types/common.types";
import { formatDate } from "@/utils/date";
import {
  ArrowLeftIcon,
  ArrowUpDownIcon,
  Download,
  ListFilter,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import DataSection from "../common/DataSection";
import Text from "../common/Text";
import useAnalyticsManager from "@/hooks/miscellaneous/useAnalyticsManager";
import { useJobViewStore } from "@/zustand/store";
import JobViewSwitch from "./JobViewSwitch";
import SearchInput from "../common/SearchInput";
import { useDebounceValue } from "usehooks-ts";
import { VisibilityState } from "@tanstack/react-table";
import MultiSelect from "../common/MultiSelectDropdown";
import { Button } from "../ui/button";
import Icon from "../common/Icon";
import { useDownloadJobStylesQuery } from "@/hooks/queries/useDownloadJobStylesQuery";
import { downloadFile } from "@/utils/downloadFile";
import { useQueryClient } from "@tanstack/react-query";
import { JobSKUDataTable, JobStyleIdDataTable } from "./table";

type JobDetailProps = {
  job: ScheduledJob;
  onBack: () => void;
};

const getJobDetails = (job: ScheduledJob): DataItem<string>[][] => {
  return [
    [{ label: "Updated On:", value: formatDate(job.lastExecutionTime) }],
    [
      { label: "Job Title:", value: job.jobName },
      { label: "Start Date:", value: formatDate(job.startTime) },
      { label: "End Date:", value: formatDate(job.endTime) },
    ],
  ];
};

const JobDetail: React.FC<JobDetailProps> = ({ job, onBack }) => {
  const queryClient = useQueryClient();

  const jobDetails = useMemo(() => getJobDetails(job), [job]);
  const { sendEvent } = useAnalyticsManager();
  const { isAggregated } = useJobViewStore();

  const [search, setSearch] = useState("");
  const [debouncedSearchValue] = useDebounceValue(search, 500);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [columnOptions, setColumnOptions] = useState<
    Array<{ id: string; label: string }>
  >([]);
  const [initialSelectedColumns, setInitialSelectedColumns] = useState<
    string[]
  >([]);

  const [searchSKU, setSearchSKU] = useState("");
  const [debouncedSearchValueSKU] = useDebounceValue(searchSKU, 500);
  const [columnVisibilitySKU, setColumnVisibilitySKU] =
    useState<VisibilityState>({});
  const [columnOptionsSKU, setColumnOptionsSKU] = useState<
    Array<{ id: string; label: string }>
  >([]);
  const [initialSelectedColumnsSKU, setInitialSelectedColumnsSKU] = useState<
    string[]
  >([]);

  const handleColumnChange = (selected: string[]) => {
    sendEvent("edit_columns_clicked");
    const newColumnVisibility = Object.fromEntries(
      columnOptions.map((option) => [option.id, selected.includes(option.id)])
    );
    setColumnVisibility(newColumnVisibility);
  };

  const handleColumnChangeSKU = (selected: string[]) => {
    const newColumnVisibility = Object.fromEntries(
      columnOptionsSKU.map((option) => [
        option.id,
        selected.includes(option.id),
      ])
    );
    setColumnVisibilitySKU(newColumnVisibility);
  };

  const [downloadExcel, setDownloadExcel] = useState(false);
  const [downloadExcelSKU, setDownloadExcelSKU] = useState(false);
  const {
    data: excelData,
    isLoading: isDownloading,
    isSuccess,
  } = useDownloadJobStylesQuery(job.id, {
    enabled: downloadExcel && job.id !== null,
  });

  const {
    data: excelDataSKU,
    isLoading: isDownloadingSKU,
    isSuccess: isSuccessSKU,
  } = useDownloadJobStylesQuery(job.id, {
    enabled: downloadExcelSKU && job.id !== null,
  });

  useEffect(() => {
    if (isSuccess && downloadExcel) {
      downloadFile(excelData, `${job.jobName}.csv`);
      setDownloadExcel(false);
    }
  }, [isSuccess, excelData, job.jobName, downloadExcel]);

  useEffect(() => {
    if (isSuccessSKU && downloadExcelSKU) {
      downloadFile(excelDataSKU, `${job.jobName}.csv`);
      setDownloadExcelSKU(false);
    }
  }, [isSuccessSKU, excelDataSKU, job.jobName, downloadExcelSKU]);

  const onDownload = () => {
    setDownloadExcel(true);
  };

  const onDownloadSKU = () => {
    setDownloadExcelSKU(true);
  };

  useEffect(() => {
    if (debouncedSearchValue) {
      sendEvent("job_search_initiated", {
        search_query: debouncedSearchValue,
        screen_name: "job detail",
      });
    }
  }, [debouncedSearchValue]);

  useEffect(() => {
    if (debouncedSearchValueSKU) {
      sendEvent("style_sku_search_initiated", {
        search_query: debouncedSearchValueSKU,
      });
    }
  }, [debouncedSearchValueSKU]);
  useEffect(() => {
    // Cleanup queries when view changes
    return () => {
      if (isAggregated) {
        queryClient.removeQueries({ queryKey: ["jobStyleDataAggregated"] });
      } else {
        queryClient.removeQueries({ queryKey: ["jobStyleData"] });
      }
    };
  }, [isAggregated, queryClient]);

  if (isAggregated) {
    return (
      <>
        <div className="flex w-full flex-1 items-start gap-2">
          <ArrowLeftIcon
            className="mt-2 cursor-pointer text-[#1C1B1F]"
            onClick={() => {
              sendEvent("back_to_job_list_clicked", {
                job_id: job.id,
              });

              onBack();
            }}
          />
          <div className="flex w-full flex-col gap-4">
            <h1 className="font-display text-[34px] font-medium leading-[1.05] tracking-[-0.02em] text-brand-text">
              Home.
            </h1>
            <div className="flex flex-wrap items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-brand-muted">
              <span>Jobs</span>
              <span className="text-brand-border">/</span>
              <span>Style ID</span>
            </div>
            <div className="flex items-center justify-end gap-4">
              <div className="flex items-center justify-end gap-4">
                <SearchInput
                  value={search}
                  onChange={setSearch}
                  placeholder="Search Dataset"
                />
                <Button
                  variant="outline"
                  className="flex items-center justify-center gap-2 border-brand-border font-mono text-[11px] uppercase tracking-[0.22em] text-brand-text"
                  leftIcon={<ListFilter className="h-4 w-4" />}
                  onClick={() => {}}
                  isLoading={isDownloading}
                >
                  Filter
                </Button>
                <Button
                  variant="outline"
                  className="flex items-center justify-center gap-2 border-brand-border font-mono text-[11px] uppercase tracking-[0.22em] text-brand-text"
                  leftIcon={
                    <ArrowUpDownIcon className="h-4 w-4" />
                  }
                  onClick={() => {}}
                  isLoading={isDownloading}
                >
                  Sort
                </Button>
                <Button
                  variant="outline"
                  className="flex items-center justify-center gap-2 border-brand-border font-mono text-[11px] uppercase tracking-[0.22em] text-brand-text"
                  leftIcon={<Download className="h-4 w-4" />}
                  onClick={onDownload}
                  isLoading={isDownloading}
                >
                  Export Data
                </Button>
                <div className="flex gap-3">
                  {columnOptions.length > 0 && (
                    <MultiSelect
                      options={columnOptions}
                      initialSelected={initialSelectedColumns}
                      onChange={handleColumnChange}
                      trigger={
                        <Button
                          variant="outline"
                          className="flex items-center justify-center gap-2 border-brand-border font-mono text-[11px] uppercase tracking-[0.22em] text-brand-text"
                          leftIcon={<Icon name="edit" size={16} />}
                        >
                          Edit Column
                        </Button>
                      }
                      screen_name="job_styleID"
                    />
                  )}
                </div>

                <JobViewSwitch />
              </div>
            </div>
          </div>
        </div>
        <DataSection dataRows={jobDetails} className="mb-5 mt-6" />

        <JobStyleIdDataTable
          onStyleIdClick={() => {}}
          job={job}
          search={search}
          setSearch={setSearch}
          debouncedSearchValue={debouncedSearchValue}
          columnVisibility={columnVisibility}
          setColumnVisibility={setColumnVisibility}
          setColumnOptions={setColumnOptions}
          setInitialSelectedColumns={setInitialSelectedColumns}
          isDownloading={isDownloading}
          onDownload={onDownload}
        />
      </>
    );
  }

  return (
    <>
      <div className="flex w-full flex-1 items-start gap-2">
        <ArrowLeftIcon
          className="mt-2 cursor-pointer text-[#1C1B1F]"
          onClick={() => {
            sendEvent("back_to_job_list_clicked", {
              job_id: job.id,
            });

            onBack();
          }}
        />
        <div className="flex w-full flex-col gap-4">
          <h1 className="font-display text-[34px] font-medium leading-[1.05] tracking-[-0.02em] text-brand-text">
            Home.
          </h1>
          <div className="flex flex-wrap items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-brand-muted">
            <span>Jobs</span>
            <span className="text-brand-border">/</span>
            <span>Style ID</span>
            <span className="text-brand-border">/</span>
            <span>SKU ID</span>
          </div>
          <div className="flex items-center justify-end gap-4">
            <div className="flex items-center justify-end gap-4">
              <SearchInput value={searchSKU} onChange={setSearchSKU} />
              <Button
                variant="outline"
                className="flex items-center justify-center gap-2 border-brand-border font-mono text-[11px] uppercase tracking-[0.22em] text-brand-text"
                leftIcon={<ListFilter className="h-4 w-4" />}
                onClick={() => {}}
              >
                Filter
              </Button>
              <Button
                variant="outline"
                className="flex items-center justify-center gap-2 border-brand-border font-mono text-[11px] uppercase tracking-[0.22em] text-brand-text"
                leftIcon={
                  <ArrowUpDownIcon className="h-4 w-4" />
                }
                onClick={() => {}}
                isLoading={isDownloadingSKU}
              >
                Sort
              </Button>
              <Button
                variant="outline"
                className="flex items-center justify-center gap-2 border-brand-border font-mono text-[11px] uppercase tracking-[0.22em] text-brand-text"
                leftIcon={<Download className="h-4 w-4" />}
                onClick={onDownloadSKU}
                isLoading={isDownloadingSKU}
              >
                Export Data
              </Button>
              <div className="flex gap-3">
                {columnOptionsSKU.length > 0 && ( // Only show when options exist
                  <MultiSelect
                    options={columnOptionsSKU}
                    initialSelected={initialSelectedColumnsSKU}
                    onChange={handleColumnChangeSKU}
                    trigger={
                      <Button
                        variant="outline"
                        className="flex items-center justify-center gap-2 border-brand-border font-mono text-[11px] uppercase tracking-[0.22em] text-brand-text"
                        leftIcon={<Icon name="edit" size={16} />}
                      >
                        Edit Column
                      </Button>
                    }
                    screen_name="job_sku"
                  />
                )}
              </div>

              <JobViewSwitch />
            </div>
          </div>
        </div>
      </div>
      <DataSection dataRows={jobDetails} className="mb-5 mt-6" />

      <JobSKUDataTable
        onSkuClick={() => {}}
        job={job}
        search={searchSKU}
        setSearch={setSearchSKU}
        debouncedSearchValue={debouncedSearchValueSKU}
        columnVisibility={columnVisibilitySKU}
        setColumnVisibility={setColumnVisibilitySKU}
        setColumnOptions={setColumnOptionsSKU}
        setInitialSelectedColumns={setInitialSelectedColumnsSKU}
        isDownloading={isDownloadingSKU}
        onDownload={onDownloadSKU}
      />
    </>
  );
};

export default JobDetail;
